import {Component, ContentChild, OnInit, inject, input, signal, TemplateRef} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {NgTemplateOutlet} from '@angular/common';
import {map} from 'rxjs/operators';

import {BROWSE_PORTAL} from '../../queries-signiant';
import {Portal, PortalEntry} from '../../types-signiant';
import {CldatePipe} from '../../pipes/cldate.pipe';
import {FileSizePipe} from '../../pipes/file-size-pipe';
import {TreeRow, collapseRow, expandRow, findRow, newRow, patchRow, rowKey}
  from './portal-tree';

export {TreeRow} from './portal-tree';

/**
 * Browses a Signiant portal's own file tree: click a folder, it expands in place.
 *
 * Shared by both portal types on purpose: what is on the portal is the same
 * question either way — an acquisition portal offers files to import, a delivery
 * portal shows whether what we sent actually arrived. Only the per-row action
 * differs, and that is projected in by the parent
 * (`<ng-template let-entry>` … `</ng-template>`).
 *
 * **Children are fetched per folder, on first expand.** Not a UX preference: the
 * portal REST API costs one request per directory, so listing a whole tree
 * inside one request overruns the resolver's 30 s budget — measured on
 * thales-medianet, which has 21 top-level directories and cannot be walked that
 * way at all. One folder is one request, whatever the portal's size, and an
 * expanded folder is not re-fetched.
 *
 * The row list itself is manipulated by the pure functions in `portal-tree.ts`,
 * which are unit-tested. Rows are addressed by `rowKey`, never by object
 * identity — see the note there.
 */
@Component({
  selector: 'app-portal-browser',
  templateUrl: './portal-browser.component.html',
  imports: [CldatePipe, FileSizePipe, NgTemplateOutlet],
})
export class PortalBrowserComponent implements OnInit {
  portalId = input.required<string>();
  /** Header of the action column; no action template means no column. */
  actionHeader = input('');

  /** Projected per-row action, receiving the entry as `$implicit`. */
  @ContentChild(TemplateRef) actionTemplate?: TemplateRef<{$implicit: PortalEntry}>;

  rows = signal<TreeRow[]>([]);
  loading = signal(false);
  /** Signed out and empty are different answers and read differently. */
  noSession = signal(false);
  error = signal<string | null>(null);

  private apollo = inject(Apollo);

  /**
   * Loaded once, from the input. The portal detail page reads its own id from
   * the route *snapshot*, so a different portal means a rebuilt page; following
   * that same assumption here keeps the two consistent — and avoids the trap
   * this component had when it used an effect: `load()` read the signal it also
   * wrote, so every expand re-ran the effect and snapped the view back to the
   * root.
   */
  ngOnInit(): void {
    this.loadRoot();
  }

  /** Expand or collapse a folder. Files are not clickable. */
  toggle(row: TreeRow): void {
    const key = rowKey(row);
    const current = findRow(this.rows(), key);
    if (!current || !current.entry.isDirectory || current.loading) {
      return;
    }
    if (current.expanded) {
      this.rows.update(rows => collapseRow(rows, key));
    } else {
      this.expand(key, current.entry);
    }
  }

  reload(): void {
    this.loadRoot();
  }

  private expand(key: string, entry: PortalEntry): void {
    this.rows.update(rows => patchRow(rows, key, {loading: true, error: undefined}));
    this.fetch(entry.path, entry.folderId).subscribe({
      next: (entries) => {
        if (entries == null) {
          // The API could not list at all — the session died since the root was
          // loaded. Say so on the row rather than showing an empty folder.
          this.noSession.set(true);
          this.rows.update(rows => patchRow(rows, key, {
            loading: false, error: 'no portal session — log in again',
          }));
          return;
        }
        this.rows.update(rows => expandRow(rows, key, entries));
      },
      error: (err) => this.rows.update(rows => patchRow(rows, key, {
        loading: false, error: err?.message ?? String(err),
      })),
    });
  }

  private loadRoot(): void {
    this.loading.set(true);
    this.error.set(null);
    this.fetch(null, null).subscribe({
      next: (entries) => {
        this.noSession.set(entries == null);
        this.rows.set((entries ?? []).map(e => newRow(e, 0)));
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message ?? String(err));
        this.loading.set(false);
      },
    });
  }

  /** One directory. Emits null when the portal could not be listed at all. */
  private fetch(path: string | null, folderId: string | null) {
    return this.apollo.use('signiant')
      .query<{portal: Portal}>({
        query: BROWSE_PORTAL,
        variables: {portalId: this.portalId(), path: path || null, folderId: folderId || null},
        fetchPolicy: 'network-only',
      })
      .pipe(map(({data}) => data?.portal?.browse ?? null));
  }
}
